<template>
  <div
    class="tile"
    :class="{
      selected,
      inverted,
      'always-show-checkbox': alwaysShowCheckbox,
      'selection-disabled': selectionDisabled,
    }"
  >
    <div class="image-wrapper">
      <Checkbox
        v-if="isSelectable"
        class="tile-checkbox"
        :model-value="selected"
        :disabled="selectionDisabled"
        @update:model-value="$emit('toggleSelection')"
      />
      <div v-if="contextMenuOptions.length > 0" class="context-menu">
        <Dropdown
          id="context-menu"
          :items="contextMenuOptions"
          :item-to-string="(item) => item.name"
          :item-key="(item) => item.key"
          :selected-highlighting="false"
          @change="(item) => $emit('contextMenuClick', item)"
        >
          <template
            #dropdown-button="{ toggleMenu, isOpen, onKeyDown, buttonId }"
          >
            <div
              :id="buttonId"
              class="dropdown-button context-menu"
              @keydown="onKeyDown"
            >
              <label
                role="button"
                aria-haspopup="true"
                :aria-expanded="isOpen"
                @click="toggleMenu()"
              >
                <Icon icon="more-vertical" />
              </label>
            </div>
          </template>
        </Dropdown>
      </div>

      <div
        class="preview-image"
        @click="clickable && !selectionDisabled && $emit('onClick')"
      >
        <slot name="image">
          <div v-if="image || icon">
            <div
              v-if="image"
              class="preview-image-container"
              :class="{
                'design-checked-background': tiledBackground,
                dark: darkImageBg,
              }"
            >
              <img :src="image" :alt="name" />
            </div>
            <div v-else class="preview-image-container icon-only">
              <Icon :icon="icon" />
              <p>{{ $t('GENERAL.NO_PREVIEW') }}</p>
            </div>
          </div>
        </slot>
        <div v-if="clickable" class="image-overlay">
          <div>
            <div class="overlay-content">
              <div class="edit-icon">
                <Icon :icon="imageOverlayIcon || 'pen'" />
              </div>
            </div>
          </div>
        </div>
        <div v-if="badges?.length" class="badges">
          <Badge
            v-for="badge in badges.slice(0, 2)"
            :key="badge.label"
            :type="badge.type"
          >
            {{ badge.label }}
          </Badge>
        </div>
      </div>

      <slot name="overlay"></slot>
    </div>
    <div class="card-footer" :class="{ 'dynamic-height': !tiledBackground }">
      <strong class="footer-name text-truncate">{{ name }}</strong>
      <div class="status">
        <div v-if="displayStatus" class="main-status">
          <slot name="status"></slot>
        </div>

        <slot name="sideinfo"></slot>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import Checkbox from 'commons/checkbox/Checkbox.vue';
import Badge from 'src/app/components/badge/Badge.vue';
import type { TileBadge } from 'src/app/components/badge/badgeTypes';
import Dropdown from 'commons/Dropdown/Dropdown.vue';

export default {
  name: 'Tile',
  components: {
    Checkbox,
    Badge,
    Dropdown,
  },
  props: {
    name: {
      type: String,
    },
    inverted: {
      type: Boolean,
    },
    isSelectable: {
      type: Boolean,
    },
    alwaysShowCheckbox: {
      type: Boolean,
    },
    selected: {
      type: Boolean,
    },
    image: {
      type: String,
    },
    darkImageBg: {
      type: Boolean,
    },
    icon: {
      type: String,
    },
    clickable: {
      type: Boolean,
    },
    imageOverlayIcon: {
      type: String,
    },
    displayStatus: {
      type: Boolean,
      default: true,
    },
    tiledBackground: {
      type: Boolean,
      default: true,
    },
    /** At most the first two are rendered. */
    badges: {
      type: Array as PropType<TileBadge[]>,
    },
    contextMenuOptions: {
      type: Array,
      default: () => [],
    },
    /**
     * The tile is shown but cannot be selected — e.g. a selection limit is already reached. Only
     * dims and blocks selection; the context menu stays usable.
     */
    selectionDisabled: {
      type: Boolean,
    },
  },
  emits: ['toggleSelection', 'onClick', 'contextMenuClick'],
};
</script>

<style lang="scss" scoped>
@use 'sass:math';

@import 'src/scss/styleguide/mixins/media';
@import 'src/scss/styleguide/colors';
@import 'src/scss/constants';

.tile {
  overflow: hidden;
  background-color: white;
  display: flex;
  flex-direction: column;
  border-radius: $border-radius-medium;
  position: relative;
  border: 1px solid $grey15;

  &.inverted {
    background-color: $grey5;
  }

  &.selected {
    box-shadow: 0 0 0 2px $pa-color-main;

    .tile-checkbox {
      display: block;
    }
  }

  &.always-show-checkbox {
    .tile-checkbox {
      display: block;
    }
  }

  &.selection-disabled {
    .preview-image,
    .card-footer {
      opacity: 0.4;
    }

    .preview-image {
      cursor: default;
    }

    .image-overlay {
      display: none;
    }
  }

  :deep(.checkbox-container) .checkmark {
    border-radius: $border-radius-small;
  }

  :deep(.preview-image) {
    cursor: pointer;

    & > div:first-child::before {
      content: '';
      display: block;
      padding-top: math.div(100, math.div(300, 300)) * 1%;
    }
  }

  .card-footer {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    height: 90px;
    width: 100%;
    padding: 16px;
    border: none;
    border-top: 1px solid $grey15;

    &.condensed {
      height: 50px;
    }

    .footer-name {
      width: 100%;
    }

    &.dynamic-height {
      padding: 8px 12px 12px 12px;
      height: auto;
    }
  }
}

.badges {
  display: flex;
  z-index: 1;
  position: absolute;
  bottom: 8px;
  left: 8px;
  gap: 4px;
  font-size: 12px;
}

.context-menu {
  display: none;

  .dropdown-button {
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 5;
  }

  :deep(.dropdown-items) {
    width: auto;
    min-width: auto;
    max-width: calc(100% - 16px);
    position: absolute;
    top: 32px;
    right: 8px;
  }

  label {
    cursor: pointer;
    background-color: $white;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: $border-radius-medium;
  }
}

.image-wrapper {
  position: relative;

  .tile-checkbox {
    position: absolute;
    top: 8px;
    left: 8px;
    z-index: 3;
    display: none;
  }

  &:hover {
    .image-overlay {
      display: block;
    }

    .tile-checkbox,
    .context-menu {
      display: block;
    }
  }
}

.status {
  display: flex;
  width: 100%;
  align-items: center;
  margin-top: auto;

  .main-status {
    flex-grow: 1;
  }
}

.preview-image-container {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  align-items: center;
  display: flex;
  justify-content: center;
  padding: 8px;

  img {
    object-fit: contain;
    width: 100%;
    height: 100%;
  }

  &:not(&.design-checked-background) {
    background-color: $grey10;
  }

  &.icon-only {
    background-color: $grey10;
    flex-direction: column;

    .icon {
      width: 32px;
      height: 32px;
    }

    p {
      margin: 8px 0 0 0;
    }
  }
}

.image-overlay {
  display: none;
  position: absolute;
  width: 100%;
  height: 100%;
  background-color: rgba(21, 54, 84, 0.3);
  top: 0;
  left: 0;
  z-index: 2;

  & > div {
    display: flex;
    width: 100%;
    height: 100%;
    align-items: center;
    justify-content: center;

    .overlay-content {
      display: flex;
      flex-direction: column;
      align-items: center;

      .edit-icon {
        background-color: white;
        border-radius: 50%;
        padding: 5px;
        width: 60px;
        height: 60px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: $pa-color-main;

        .icon {
          width: 24px;
          height: 24px;
        }
      }
    }
  }
}
</style>
